<nz-layout class="discharge-report-page">
  <nz-header>
    <div class="editor-header">
      <div class="editor-title">
        {{ "Discharge Report Preview" | translate }}
      </div>
      <div class="editor-actions">
        @if (canIssue && !showIssueConfirmation) {
          <button
            nz-button
            class="issue-report-btn"
            type="button"
            (click)="openIssueConfirmation()"
          >
            <i nz-icon nzType="file-protect" nzTheme="outline"></i>
            {{ "Issue Report" | translate }}
          </button>
        }
        @if (canDownloadPdf) {
          <button
            nz-button
            class="download-pdf-btn"
            type="button"
            [nzLoading]="isDownloadingPdf"
            (click)="handleDownloadPdf()"
          >
            <i nz-icon nzType="download" nzTheme="outline"></i>
            {{ "Download PDF" | translate }}
          </button>
        }
        <button
          nz-button
          class="cancel-btn"
          type="button"
          (click)="handleCancel()"
        >
          {{ "Cancel" | translate }}
        </button>
      </div>
    </div>
  </nz-header>

  <nz-content>
    @if (showIssueConfirmation) {
      <div class="discharge-report-issue-confirmation">
        <div class="discharge-report-issue-confirmation-title">
          <i nz-icon nzType="exclamation-circle" nzTheme="outline"></i>
          {{
            "Issuing this discharge report is final and cannot be undone."
              | translate
          }}
        </div>
        <form
          [formGroup]="issueForm"
          class="discharge-report-issue-confirmation-form"
        >
          <label nz-checkbox formControlName="attestationConfirmed">
            {{
              "I have reviewed this discharge report and confirm it is accurate and complete."
                | translate
            }}
          </label>
          <label nz-checkbox formControlName="legalAuthenticationConfirmed">
            {{
              "I am legally authorized to sign and finalize this document."
                | translate
            }}
          </label>
          <div class="discharge-report-issue-confirmation-actions">
            <button
              nz-button
              class="issue-confirm-btn"
              type="button"
              [disabled]="issueForm.invalid"
              [nzLoading]="isIssuing"
              (click)="confirmIssue()"
            >
              {{ "Confirm & Issue" | translate }}
            </button>
            <button
              nz-button
              class="cancel-btn"
              type="button"
              [disabled]="isIssuing"
              (click)="cancelIssueConfirmation()"
            >
              {{ "Cancel" | translate }}
            </button>
          </div>
        </form>
      </div>
    }

    @if (isLoading) {
      <div class="discharge-report-state discharge-report-state-loading">
        <nz-spin
          nzSimple
          [nzTip]="'Loading discharge report preview' | translate"
        ></nz-spin>
      </div>
    }

    @if (!isLoading && error?.status === "ineligible") {
      <div class="discharge-report-state discharge-report-state-error">
        <i nz-icon nzType="exclamation-circle" nzTheme="outline"></i>
        <span>{{
          error?.message ||
            ("This Episode of Care is not eligible for a discharge report"
              | translate)
        }}</span>
      </div>
    }

    @if (!isLoading && error?.status === "error") {
      <div class="discharge-report-state discharge-report-state-error">
        <i nz-icon nzType="close-circle" nzTheme="outline"></i>
        <span>{{
          error?.message ||
            ("Unable to generate the discharge report preview" | translate)
        }}</span>
      </div>
      @if (error?.issues?.length) {
        <app-discharge-report-validation-summary
          [issues]="error!.issues"
          [warnings]="[]"
        />
      }
    }

    @if (!isLoading && !error && data) {
      @let report = data.report;
      @let body = report.body;

      <app-discharge-report-header
        [report]="report"
        [previewState]="data.preview"
        [issuedReport]="data.issuedReport"
      />

      @if (validationIssues.length || validationWarnings.length) {
        <app-discharge-report-validation-summary
          [issues]="validationIssues"
          [warnings]="validationWarnings"
        />
      }

      @if (body?.alerts?.generatedNarrative || alertRows.length) {
        <app-discharge-report-section [title]="'Alerts' | translate">
          @if (alertRows.length) {
            <app-reusable-table
              [columns]="['Alert', 'Status']"
              [rowFields]="['alert', 'status']"
              [data]="alertRows"
              [isLoading]="isLoading"
              [isPreview]="true"
              [pagination]="false"
            />
          }
          @if (splitNarrative(body?.alerts?.generatedNarrative); as narrative) {
            <p class="discharge-report-narrative">
              @if (narrative.label) {
                <span class="discharge-report-inline-label"
                  >{{ narrative.label }}:</span
                >
              }
              {{ narrative.rest }}
            </p>
          }
        </app-discharge-report-section>
      }

      @if (
        body?.encounterInformation?.encounter?.period ||
        body?.encounterInformation?.encounter?.reasonString?.length ||
        admitterName ||
        body?.encounterInformation?.note
      ) {
        <app-discharge-report-section
          [title]="'Encounter Information' | translate"
        >
          @if (body?.encounterInformation?.encounter?.period; as period) {
            <p class="discharge-report-meta-line">
              @if (period.start) {
                <span class="discharge-report-inline-label"
                  >{{ "Start" | translate }}:</span
                >
                {{ formatDate(period.start) }}
              }
              @if (period.end) {
                &nbsp;&ndash;
                <span class="discharge-report-inline-label"
                  >{{ "End" | translate }}:</span
                >
                {{ formatDate(period.end) }}
              }
            </p>
          }
          @if (body?.encounterInformation?.encounter?.reasonString?.length) {
            <p class="discharge-report-meta-line">
              <span class="discharge-report-inline-label"
                >{{ "Reason" | translate }}:</span
              >
              {{ body?.encounterInformation?.encounter?.reasonString?.join(", ") }}
            </p>
          }
          @if (admitterName) {
            <p class="discharge-report-meta-line">
              <span class="discharge-report-inline-label"
                >{{ "Admitted By" | translate }}:</span
              >
              {{ admitterName }}
            </p>
          }
          @if (body?.encounterInformation?.note) {
            <p class="discharge-report-note">
              <span class="discharge-report-inline-label"
                >{{ "Note" | translate }}:</span
              >
              {{ body?.encounterInformation?.note }}
            </p>
          }
        </app-discharge-report-section>
      }

      @if (
        body?.admissionEvaluation?.generatedNarrative ||
        body?.admissionEvaluation?.note
      ) {
        <app-discharge-report-section
          [title]="'Admission Evaluation' | translate"
        >
          @if (
            splitNarrative(body?.admissionEvaluation?.generatedNarrative);
            as narrative
          ) {
            <p class="discharge-report-narrative">
              @if (narrative.label) {
                <span class="discharge-report-inline-label"
                  >{{ narrative.label }}:</span
                >
              }
              {{ narrative.rest }}
            </p>
          }
          @if (body?.admissionEvaluation?.note) {
            <p class="discharge-report-note">
              <span class="discharge-report-inline-label"
                >{{ "Note" | translate }}:</span
              >
              {{ body?.admissionEvaluation?.note }}
            </p>
          }
        </app-discharge-report-section>
      }

      @if (pastProblemRows.length) {
        <app-reusable-table
          title="Past Problems"
          [columns]="['Diagnosis', 'Status', 'Resolution']"
          [rowFields]="['diagnosis', 'status', 'resolution']"
          [data]="pastProblemRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (pastProcedureRows.length) {
        <app-reusable-table
          title="Past Procedures"
          [columns]="['Procedure', 'Note']"
          [rowFields]="['procedure', 'note']"
          [data]="pastProcedureRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (pastDeviceRows.length) {
        <app-reusable-table
          title="Device and Implants"
          [columns]="['Device', 'Note']"
          [rowFields]="['device', 'note']"
          [data]="pastDeviceRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (conditionRows.length) {
        <app-reusable-table
          title="Diagnoses"
          [columns]="['Diagnosis', 'Status']"
          [rowFields]="['diagnosis', 'status']"
          [data]="conditionRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (procedureRows.length) {
        <app-reusable-table
          title="Procedures"
          [columns]="['Procedure', 'Note']"
          [rowFields]="['procedure', 'note']"
          [data]="procedureRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (deviceRows.length) {
        <app-reusable-table
          title="Medical Devices"
          [columns]="['Device', 'Note']"
          [rowFields]="['device', 'note']"
          [data]="deviceRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (pharmacotherapyRows.length) {
        <app-reusable-table
          title="Pharmacotherapy"
          [columns]="[
            'Medication',
            'Status',
            'Dosage',
            'Onset Date & Time',
            'End Date & Time',
          ]"
          [rowFields]="[
            'medication',
            'status',
            'dosage',
            'onSetDateTime',
            'endDateTime',
          ]"
          [data]="pharmacotherapyRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (
        body?.dischargeDetails?.generatedNarrative ||
        body?.dischargeDetails?.note
      ) {
        <app-discharge-report-section [title]="'Discharge Details' | translate">
          @if (
            splitNarrative(body?.dischargeDetails?.generatedNarrative);
            as narrative
          ) {
            <p class="discharge-report-narrative">
              @if (narrative.label) {
                <span class="discharge-report-inline-label"
                  >{{ narrative.label }}:</span
                >
              }
              {{ narrative.rest }}
            </p>
          }
          @if (body?.dischargeDetails?.note) {
            <p class="discharge-report-note">
              <span class="discharge-report-inline-label"
                >{{ "Note" | translate }}:</span
              >
              {{ body?.dischargeDetails?.note }}
            </p>
          }
        </app-discharge-report-section>
      }

      @if (medicationSummaryRows.length) {
        <app-reusable-table
          title="Medication Summary"
          [columns]="[
            'Medication',
            'Status',
            'Change',
            'Dosage',
            'Onset Date & Time',
            'End Date & Time',
          ]"
          [rowFields]="[
            'medication',
            'status',
            'change',
            'dosage',
            'onSetDateTime',
            'endDateTime',
          ]"
          [data]="medicationSummaryRows"
          [isLoading]="isLoading"
          [isPreview]="true"
          [pagination]="false"
        />
      }

      @if (carePlanRows.length || body?.carePlan?.otherRecommendations) {
        <app-discharge-report-section [title]="'Care Plan' | translate">
          @if (carePlanRows.length) {
            <app-reusable-table
              [columns]="[
                'Plan',
                'Description',
                'Activity',
                'Period',
                'Status',
              ]"
              [rowFields]="[
                'plan',
                'description',
                'activity',
                'period',
                'status',
              ]"
              [data]="carePlanRows"
              [isLoading]="isLoading"
              [isPreview]="true"
              [pagination]="false"
            />
          }
          @if (body?.carePlan?.otherRecommendations) {
            <p class="discharge-report-note">
              <span class="discharge-report-inline-label"
                >{{ "Note" | translate }}:</span
              >
              {{ body?.carePlan?.otherRecommendations }}
            </p>
          }
        </app-discharge-report-section>
      }

      @if (attachments.length) {
        <app-discharge-report-section [title]="'Attachments' | translate">
          <div class="doc-list">
            @for (attachment of attachments; track $index) {
              <div class="doc-row">
                <div class="left">
                  <i
                    nz-icon
                    [nzType]="attachmentIcon(attachment)"
                    nzTheme="outline"
                  ></i>
                  <div class="meta">
                    <div class="name" [title]="attachment.title">
                      {{ attachment.title }}
                    </div>
                    <div class="sub">
                      @if (attachmentSizeText(attachment)) {
                        {{ attachmentSizeText(attachment) }}
                      }
                      @if (
                        attachmentSizeText(attachment) &&
                        attachmentTypeText(attachment)
                      ) {
                        &nbsp;|&nbsp;
                      }
                      @if (attachmentTypeText(attachment)) {
                        {{ attachmentTypeText(attachment) }}
                      }
                    </div>
                  </div>
                </div>

                <div class="actions">
                  <button
                    nz-button
                    nzType="default"
                    nzShape="round"
                    class="action-btn"
                    type="button"
                    (click)="viewAttachment(attachment)"
                    [disabled]="!canPreviewAttachment(attachment)"
                  >
                    <i nz-icon nzType="eye" nzTheme="outline"></i>
                    <span class="btn-text">{{ "View" | translate }}</span>
                  </button>
                </div>
              </div>
            }
          </div>
        </app-discharge-report-section>
      }
    }
  </nz-content>

  <nz-modal
    [(nzVisible)]="isPdfPreviewVisible"
    [nzTitle]="
      ('Preview' | translate) +
      (pdfPreviewFileName ? ' - ' + pdfPreviewFileName : '')
    "
    [nzFooter]="null"
    nzWidth="80vw"
    [nzBodyStyle]="{ padding: '12px 16px 20px' }"
    (nzOnCancel)="closePdfPreview()"
  >
    <ng-container *nzModalContent>
      @if (attachmentPreviewUrl) {
        <pdf-viewer
          class="attachment-pdf-viewer"
          [src]="attachmentPreviewUrl"
          [render-text]="true"
          [original-size]="false"
          [fit-to-page]="true"
          [show-all]="true"
        ></pdf-viewer>
      }
    </ng-container>
  </nz-modal>
</nz-layout>
